import {Injectable} from "@angular/core";
import {ApiService} from "app/services/domain/api.service";
import {UsersService} from "app/services/domain/users.service";
import {toEmbeddedImageDataUrl} from "framework/utils/embedded-image";
import {Lookup} from "framework/services/utils.service";
import * as Models from "app/services/proxy/model/models";

@Injectable()
export class ComplianceStandardService
{
    private m_complianceStandardsLookup: Lookup<ComplianceStandard[], string> = {};

    private readonly complianceUriToImageMap: Record<string, string> = {
        "AC3-v-01/0000"                : "AC3_abstracted_cloud_compliance_controls.png",
        "CIS-AWS-2.0/0034"             : "cis_common.png",
        "CIS-AWS-3.0/0035"             : "cis_common.png",
        "CIS-AWS-5.0/0040"             : "cis_common.png",
        "CIS-AWS-6.0/0041"             : "cis_common.png",
        "CIS-Azure-2.0/0033"           : "cis_common.png",
        "CIS-Azure-2.1/0036"           : "cis_common.png",
        "CIS-Azure-4.0/0043"           : "cis_common.png",
        "CIS-Azure-5.0/0044"           : "cis_common.png",
        "CIS-GCP-3.0/0037"             : "cis_common.png",
        "CIS-GCP-4.0/0042"             : "cis_common.png",
        "CIS-Kuber-1.23/0026"          : "cis_kuber.png",
        "CIS-OCI-2.0/0039"             : "cis_common.png",
        "CIS-OCI-3.0/0045"             : "cis_common.png",
        "CIS-Oracle-Cloud/0018"        : "cis_common.png",
        "Fedramp-Control-High/0009"    : "fedramp_high.png",
        "Fedramp-Control-Moderate/0010": "fedramp_moderate.png",
        "GCF/0020"                     : "gcp_caf.png",
        "GDPR/0019"                    : "gdpr.png",
        "HIPAA/0002"                   : "hipaa.png",
        "ISO-27001/0011"               : "iso_27001.png",
        "ISO/0001"                     : "iso_27001.png",
        "NIST-800-171/0025"            : "nist_800_171.png",
        "NISTv5/0021"                  : "nist_800_171.png",
        "NIST-SP800-53/0006"           : "nist_800_171.png",
        "PCI-DSS-4.0/0038"             : "pci_dss_v4.0.png",
        "SOC2/0017"                    : "soc2.png"
    };

    constructor(private m_api: ApiService)
    {}

    public async fetchComplianceStandardName(tenantId: string,
                                             type: string): Promise<ComplianceStandard[]>
    {
        const key = `${tenantId}-${type}`;

        if (this.m_complianceStandardsLookup[key])
        {
            return this.m_complianceStandardsLookup[key]!;
        }

        const complianceList: Models.ComplianceStandardsResponse = await this.m_api.compliance.listStandards(tenantId, type);
        const result: ComplianceStandard[]                       = complianceList.data.map((std) => ({
            uri : std.compliance_uri,
            name: std.compliance_name
        }));

        this.m_complianceStandardsLookup[key] = result;

        return result;
    }

    public getComplianceLogo(uri: string): string | undefined
    {
        return this.complianceUriToImageMap[uri];
    }

    public async getResolvedLogo(users: UsersService,
                                 uri: string,
                                 logo?: string): Promise<string>
    {
        const defaultLogo = "assets/compliance/default_logo.png";

        if (logo)
        {
            const embeddedLogo = toEmbeddedImageDataUrl(logo);
            if (embeddedLogo) return embeddedLogo;

            const fileServerLogo = users.getFileServerUrlLogo(logo, "compliance");
            return await this.validateImageUrl(fileServerLogo, defaultLogo);
        }

        const globalLogo = this.getComplianceLogo(uri);
        return globalLogo ? `assets/compliance/${globalLogo}` : defaultLogo;
    }

    private async validateImageUrl(url: string,
                                   fallback: string): Promise<string>
    {
        return new Promise(resolve =>
                           {
                               const img   = new Image();
                               img.onload  = () => resolve(url);
                               img.onerror = () => resolve(fallback);
                               img.src     = url;
                           });
    }
}

interface ComplianceStandard
{
    uri: string;
    name: string;
}